import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, } from 'chart.js';
import { useTheme } from '../contexts/ThemeContext';
import { eachDayOfInterval, format, parseISO } from 'date-fns';
import { Card } from './ui/card';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
const ProductOEEStats = ({ data, height = 280, showProductDetails = true, deviceGroupLabel = 'Production', qualitySummaryData, }) => {
    const { isDark } = useTheme();
    const [activeView, setActiveView] = useState('overview');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const day = date.getDate();
        const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
        return `${month} ${day}, ${weekday}`;
    };
    const completeDailyStats = useMemo(() => {
        const startDate = parseISO(data.timeRange.startDate);
        const endDate = parseISO(data.timeRange.endDate);
        const allDates = eachDayOfInterval({ start: startDate, end: endDate });
        const existingStatsMap = new Map();
        data.summary.dailyStats.forEach((stat) => {
            existingStatsMap.set(stat.date, stat);
        });
        const completeStats = allDates.map((date) => {
            const dateString = format(date, 'yyyy-MM-dd');
            const existingStat = existingStatsMap.get(dateString);
            if (existingStat) {
                return existingStat;
            }
            return {
                date: dateString,
                goodCount: 0,
                rejectCount: 0,
                totalCount: 0,
                qualityRate: 0,
                __typename: 'ProductOEEStatsDaily',
            };
        });
        return completeStats;
    }, [data.timeRange.startDate, data.timeRange.endDate, data.summary.dailyStats]);
    const overviewDailyStats = useMemo(() => {
        if (qualitySummaryData && qualitySummaryData.length > 0) {
            const startDate = parseISO(data.timeRange.startDate);
            const endDate = parseISO(data.timeRange.endDate);
            const allDates = eachDayOfInterval({ start: startDate, end: endDate });
            const qualitySummaryMap = new Map();
            qualitySummaryData.forEach((stat) => {
                qualitySummaryMap.set(stat.reportDate, stat);
            });
            const completeStats = allDates.map((date) => {
                const dateString = format(date, 'yyyy-MM-dd');
                const qualitySummary = qualitySummaryMap.get(dateString);
                if (qualitySummary) {
                    return Object.assign({ date: dateString, goodCount: qualitySummary.nonRejectTotal, rejectCount: qualitySummary.rejectTotal, totalCount: qualitySummary.totalCount, qualityRate: qualitySummary.qualityRate, __typename: 'ProductOEEStatsDaily' }, (qualitySummary.isFallbackData && {
                        isFallbackData: qualitySummary.isFallbackData,
                    }));
                }
                return {
                    date: dateString,
                    goodCount: 0,
                    rejectCount: 0,
                    totalCount: 0,
                    qualityRate: 0,
                    __typename: 'ProductOEEStatsDaily',
                    isFallbackData: false,
                };
            });
            return completeStats;
        }
        return completeDailyStats;
    }, [qualitySummaryData, completeDailyStats, data.timeRange.startDate, data.timeRange.endDate]);
    const completeProductStats = useMemo(() => {
        const startDate = parseISO(data.timeRange.startDate);
        const endDate = parseISO(data.timeRange.endDate);
        const allDates = eachDayOfInterval({ start: startDate, end: endDate });
        const allDateStrings = allDates.map((date) => format(date, 'yyyy-MM-dd'));
        return data.products.map((product) => {
            const existingStatsMap = new Map();
            product.dailyStats.forEach((stat) => {
                existingStatsMap.set(stat.date, stat);
            });
            const completeDailyStats = allDateStrings.map((dateString) => {
                const existingStat = existingStatsMap.get(dateString);
                if (existingStat) {
                    return existingStat;
                }
                return {
                    date: dateString,
                    goodCount: 0,
                    rejectCount: 0,
                    totalCount: 0,
                    qualityRate: 0,
                    __typename: 'ProductOEEStatsDaily',
                };
            });
            return Object.assign(Object.assign({}, product), { dailyStats: completeDailyStats });
        });
    }, [data.products, data.timeRange.startDate, data.timeRange.endDate]);
    const handleProductClick = (productId) => {
        setSelectedProducts((prev) => {
            if (prev.includes(productId)) {
                return prev.filter((id) => id !== productId);
            }
            else {
                return [...prev, productId];
            }
        });
    };
    const clearSelection = () => {
        setSelectedProducts([]);
    };
    const getFilteredProducts = () => {
        if (selectedProducts.length === 0) {
            return completeProductStats;
        }
        return completeProductStats.filter((product) => selectedProducts.includes(product.productId));
    };
    const getTrendIndicator = (trend) => {
        switch (trend) {
            case 'up':
                return { icon: '↗', color: 'text-emerald-600 dark:text-emerald-400' };
            case 'down':
                return { icon: '↘', color: 'text-red-500 dark:text-red-400' };
            default:
                return { icon: '→', color: 'text-gray-500 dark:text-gray-400' };
        }
    };
    const tickColor = isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)';
    const gridColor = isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(0, 0, 0, 0.1)';
    const overviewChartData = {
        labels: overviewDailyStats.map((stat) => formatDate(stat.date)),
        datasets: [
            {
                label: qualitySummaryData && qualitySummaryData.length > 0
                    ? 'Total Count (Production Line Quality Summary)'
                    : 'Total Count (Non-reject)',
                data: overviewDailyStats.map((stat) => stat.goodCount || 0),
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                fill: false,
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
            },
        ],
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    color: isDark ? 'rgb(243, 244, 246)' : 'rgb(17, 24, 39)',
                    usePointStyle: true,
                    padding: 8,
                    font: { size: 10 },
                },
            },
            title: {
                display: true,
                text: `${deviceGroupLabel} — ${data.timeRange.days} Day Trends`,
                font: { size: 10 },
                color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                padding: { bottom: 6 },
            },
            tooltip: {
                callbacks: {
                    afterBody: function (context) {
                        const index = context[0].dataIndex;
                        const stat = overviewDailyStats[index];
                        if (stat) {
                            return [
                                `Non-reject Count: ${stat.goodCount}`,
                                `Reject Count: ${stat.rejectCount}`,
                                `Total Count: ${stat.totalCount}`,
                                `Quality Rate: ${stat.qualityRate.toFixed(1)}%`,
                            ];
                        }
                        return [];
                    },
                },
            },
        },
        scales: {
            x: {
                type: 'category',
                title: {
                    display: true,
                    text: 'Date',
                    font: { size: 10 },
                    color: tickColor,
                },
                ticks: {
                    color: tickColor,
                    font: { size: 10 },
                },
                grid: {
                    display: false,
                },
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Count',
                    font: { size: 10 },
                    color: tickColor,
                },
                ticks: {
                    color: tickColor,
                    font: { size: 10 },
                },
                grid: {
                    color: gridColor,
                },
            },
        },
    };
    return (_jsxs(Card, { children: [_jsxs("div", { className: "px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("span", { className: "text-xs font-medium text-gray-500 dark:text-gray-400", children: "Product Statistics" }), _jsxs("span", { className: "text-[11px] text-gray-400 dark:text-gray-500 ml-2", children: [data.timeRange.startDate, " \u2013 ", data.timeRange.endDate, " \u00B7 ", data.timeRange.days, "d \u00B7 ", data.summary.totalProducts, " products"] })] }), _jsxs("div", { className: "flex gap-1", children: [_jsx("button", { onClick: () => setActiveView('overview'), className: `px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${activeView === 'overview'
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`, children: "Overview" }), showProductDetails && (_jsxs("button", { onClick: () => setActiveView('products'), className: `px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${activeView === 'products'
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`, children: ["Products (", data.products.length, ")"] })), _jsx("button", { onClick: () => setActiveView('trends'), className: `px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${activeView === 'trends'
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`, children: "Trends" })] })] }), _jsxs("div", { className: "px-3 py-2", children: [activeView === 'overview' && (_jsx("div", { style: { height }, children: _jsx(Line, { options: chartOptions, data: overviewChartData }) })), activeView === 'products' && showProductDetails && (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "min-w-full divide-y divide-gray-100 dark:divide-gray-800", children: [_jsx("thead", { children: _jsxs("tr", { className: "bg-gray-50/90 dark:bg-gray-900/80 sticky top-0", children: [_jsx("th", { className: "px-3 py-1.5 text-left text-[11px] font-medium text-gray-500 dark:text-gray-400", children: "Product" }), _jsx("th", { className: "px-3 py-1.5 text-right text-[11px] font-medium text-gray-500 dark:text-gray-400", children: "Total" }), _jsx("th", { className: "px-3 py-1.5 text-right text-[11px] font-medium text-gray-500 dark:text-gray-400", children: "Good" }), _jsx("th", { className: "px-3 py-1.5 text-right text-[11px] font-medium text-gray-500 dark:text-gray-400", children: "Rejects" }), _jsx("th", { className: "px-3 py-1.5 text-left text-[11px] font-medium text-gray-500 dark:text-gray-400", children: "Quality" }), _jsx("th", { className: "px-3 py-1.5 text-center text-[11px] font-medium text-gray-500 dark:text-gray-400", children: "Trend" })] }) }), _jsx("tbody", { className: "divide-y divide-gray-100 dark:divide-gray-800", children: data.products.map((product) => {
                                        const productTrend = getTrendIndicator(product.trendDirection);
                                        return (_jsxs("tr", { className: "hover:bg-gray-50 dark:hover:bg-gray-900/40", children: [_jsx("td", { className: "px-3 py-1.5 whitespace-nowrap text-[11px] font-semibold text-gray-800 dark:text-gray-200", children: product.productName }), _jsx("td", { className: "px-3 py-1.5 whitespace-nowrap text-[11px] text-right text-gray-500 dark:text-gray-400 tabular-nums", children: product.totalCount.toLocaleString() }), _jsx("td", { className: "px-3 py-1.5 whitespace-nowrap text-[11px] text-right text-gray-500 dark:text-gray-400 tabular-nums", children: product.totalGoodCount.toLocaleString() }), _jsx("td", { className: "px-3 py-1.5 whitespace-nowrap text-[11px] text-right text-red-500 dark:text-red-400 tabular-nums", children: product.totalRejectCount.toLocaleString() }), _jsx("td", { className: "px-3 py-1.5 whitespace-nowrap", children: _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsxs("span", { className: "text-[11px] tabular-nums text-gray-700 dark:text-gray-300", children: [product.averageQualityRate.toFixed(1), "%"] }), _jsx("div", { className: "w-10 bg-gray-200 dark:bg-gray-700 rounded-full h-1", children: _jsx("div", { className: "bg-emerald-500 h-1 rounded-full", style: { width: `${product.averageQualityRate}%` } }) })] }) }), _jsx("td", { className: "px-3 py-1.5 whitespace-nowrap text-center", children: _jsx("span", { className: `text-xs font-semibold ${productTrend.color}`, children: productTrend.icon }) })] }, product.productId));
                                    }) })] }) })), activeView === 'trends' && (_jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between mb-1.5", children: [_jsx("span", { className: "text-[11px] text-gray-500 dark:text-gray-400", children: "Good Count by Product" }), selectedProducts.length > 0 && (_jsxs("button", { onClick: clearSelection, className: "text-[11px] text-blue-600 dark:text-blue-400 font-medium", children: ["Clear (", selectedProducts.length, ")"] }))] }), _jsx("div", { className: "max-h-16 overflow-y-auto border border-gray-100 dark:border-gray-800 rounded p-1 mb-2", children: _jsx("div", { className: "flex flex-wrap gap-1", children: data.products.map((product, index) => {
                                        const isSelected = selectedProducts.includes(product.productId);
                                        return (_jsxs("button", { onClick: () => handleProductClick(product.productId), className: `
                        inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium transition-all shrink-0
                        ${isSelected
                                                ? 'text-white'
                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}
                      `, style: Object.assign({}, (isSelected && {
                                                backgroundColor: `hsl(${(index * 360) / data.products.length}, 70%, 50%)`,
                                            })), title: product.productName, children: [_jsx("span", { className: "w-1.5 h-1.5 rounded-full mr-1 shrink-0", style: {
                                                        backgroundColor: isSelected
                                                            ? 'white'
                                                            : `hsl(${(index * 360) / data.products.length}, 70%, 50%)`,
                                                    } }), _jsx("span", { className: "truncate max-w-24", children: product.productName.length > 15
                                                        ? product.productName.substring(0, 15) + '...'
                                                        : product.productName })] }, product.productId));
                                    }) }) }), selectedProducts.length === 0 && (_jsx("div", { className: "text-[11px] text-gray-400 dark:text-gray-500 mb-1", children: "click to filter" })), _jsx("div", { style: { height }, children: _jsx(Line, { options: Object.assign(Object.assign({}, chartOptions), { plugins: Object.assign(Object.assign({}, chartOptions.plugins), { title: {
                                                display: true,
                                                text: 'Daily Good Count by Product',
                                                font: { size: 10 },
                                                color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                                            }, tooltip: {
                                                mode: 'point',
                                                intersect: false,
                                                callbacks: {
                                                    title: function (context) {
                                                        var _a;
                                                        const index = context[0].dataIndex;
                                                        const date = (_a = completeDailyStats[index]) === null || _a === void 0 ? void 0 : _a.date;
                                                        return date ? formatDate(date) : '';
                                                    },
                                                    label: function (context) {
                                                        var _a;
                                                        const productIndex = context.datasetIndex;
                                                        const filteredProducts = getFilteredProducts();
                                                        const product = filteredProducts[productIndex];
                                                        const dataIndex = context.dataIndex;
                                                        const goodCount = ((_a = product.dailyStats[dataIndex]) === null || _a === void 0 ? void 0 : _a.goodCount) || 0;
                                                        return [
                                                            `${product.productName}`,
                                                            `Good Count: ${goodCount.toLocaleString()}`,
                                                        ];
                                                    },
                                                },
                                            } }) }), data: {
                                        labels: completeDailyStats.map((stat) => formatDate(stat.date)),
                                        datasets: getFilteredProducts().map((product) => {
                                            const originalIndex = data.products.findIndex((p) => p.productId === product.productId);
                                            return {
                                                label: product.productName,
                                                data: product.dailyStats.map((stat) => stat.goodCount || 0),
                                                borderColor: `hsl(${(originalIndex * 360) / data.products.length}, 70%, 50%)`,
                                                backgroundColor: `hsla(${(originalIndex * 360) / data.products.length}, 70%, 50%, 0.1)`,
                                                fill: false,
                                                tension: 0.3,
                                                pointRadius: selectedProducts.includes(product.productId) ? 4 : 2,
                                                pointHoverRadius: selectedProducts.includes(product.productId) ? 6 : 4,
                                                borderWidth: selectedProducts.includes(product.productId) ? 2 : 1.5,
                                            };
                                        }),
                                    } }) })] }))] })] }));
};
export default ProductOEEStats;
